import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Label, Button, AlertBar, Spinner } from 'scorer-ui-kit';
import styled from 'styled-components';
import { CenterAlignedContainer, LeftButton, FieldContainer, Table, TR, TH, TD, PopupTitle, SpinnerDesign, AlertContainer, CloseIcon } from '../Style';
import Switch from 'react-switch';
import { getTargetEmails, updateTargetEmails } from '../apis';

const PopupContainer = styled.div`
    padding: 30px;
    width: 700px;
`;

const TopContainer = styled(FieldContainer)`
    margin-bottom: -15px;
    width: 154px;
    align-items: flex-start;
`;

const ToggleLabel = styled(Label)`
    padding-top: 8px;
`;

const TableContainer = styled.div`
    max-height: 360px;
    overflow-y: auto;
    overflow-x: hidden;
`;

const InfoLabel = styled(Label)`
  margin-top: -20px;
  font-size: 16px;
`;

const TableWrapper = styled.div`
  width: 100%;
  height: 260px;
  overflow-y: auto;
  margin: 20px 0 30px;
  & > table {
    margin: 0;
  }
`;

const TextWithTooltip = styled.div`
  max-width: 275px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
`;

const THContainer = styled(TH)`
  width: 275px;
  text-align: left;
`;

interface Emails {
  name: string,
  email: string,
  id: number,
  assign_to_roi: boolean
}

const EmailTargets = (props: any) => {
  const { close, roi, cameraName, getCameraInfo } = props;
  const [emails, setEmails] = useState<Emails[]>([]);
  const [enabled, setEnabled] = useState<boolean>(false);
  const [alert, setAlert] = useState<{ message: string, type: 'success' | 'error' | 'warning' | 'info' | undefined }>({ message: '', type: undefined });
  const [loading, setLoading] = useState<boolean>(true);
  const { t } = useTranslation(['DetectionSettings', 'EmailSettings', 'DeviceSettings', 'Common']);

  const getAllEmails = useCallback(() => {
    getTargetEmails(cameraName, roi.id)
      .then((res) => {
        if (res.data.status_code === '0') {
          setEmails(res.data.data.emails);
          setEnabled(res.data.data.roi.email_enabled);
          if (res.data.data.emails.length <= 0) {
            setAlert({ message: t('EmailSettings:noEmailsFound'), type: 'info' });
          }
          setLoading(false);
        } else {
          setAlert({ message: res.data.message, type: 'error' });
        }
      })
      .catch((err) => {
        if (err.response.data.message) {
          setAlert({ message: err.response.data.message, type: 'error' });
        }
        console.error(err);
      });
  }, [cameraName, roi.id, t]);

  useEffect(() => {
    getAllEmails();
  }, [getAllEmails]);

  const handleToggle = (checked: boolean) => {
    setEnabled(checked);
  };

  const onChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    let newEmails = [...emails];
    const index = emails.findIndex(({ id }) => id === parseInt(event.target.id));
    newEmails[index].assign_to_roi = event.target.checked;
    setEmails(newEmails);
  };

  const onSave = () => {
    const result = emails.filter(({ assign_to_roi }) => assign_to_roi === true).map((email) => email.id);
    const jsonData = { action: 'assign', data: { 'email_enabled': enabled, emails: result } };

    updateTargetEmails(cameraName, roi.id, jsonData)
      .then((res) => {
        if (res.data.status_code === '0') {
          setAlert({ message: t('DeviceSettings:settingsUpdatedSuccessfully'), type: 'success' });
          setTimeout(() => { getCameraInfo(); close(); }, 2000);
        } else {
          setAlert({ message: res.data.message, type: 'error' });
        }
      })
      .catch((err) => {
        if (err.response.data.message) {
          setAlert({ message: err.response.data.message, type: 'error' });
        }
        console.error(err);
      });
  };

  const onCancel = () => {
    close();
  };

  return (
    <PopupContainer>
      <CloseIcon onClick={onCancel}>x</CloseIcon>
      <CenterAlignedContainer>
        <PopupTitle>{t('EmailSettings:emailTargets')}</PopupTitle>
      </CenterAlignedContainer>
      <CenterAlignedContainer>
        <InfoLabel htmlFor='' labelText={cameraName + '/' + roi.name} />
      </CenterAlignedContainer>
      <TableContainer>
        {
          loading ?
            <Spinner size='large' styling={SpinnerDesign} /> :
            (emails && emails.length > 0) &&
              <>
                <TopContainer>
                  <ToggleLabel htmlFor='enabled' labelText={enabled ? t('enabled') : t('disabled')} />
                  <Switch name='enabled' checked={enabled} onColor='#42b0f5' uncheckedIcon={false} checkedIcon={false} onChange={handleToggle} />
                </TopContainer>
                <TableWrapper>
                  <Table>
                    <tbody>
                      <TR>
                        <THContainer bgColor='#f5f5f0'>{t('name')}</THContainer>
                        <THContainer bgColor='#f5f5f0'>{t('email')}</THContainer>
                        <TH bgColor='#f5f5f0'>{t('EmailSettings:target')}</TH>
                      </TR>
                      {
                        emails.map(({ id, name, email, assign_to_roi }) => (
                          <TR key={id}>
                            <TD textAlign='left'><TextWithTooltip title={name}>{name}</TextWithTooltip></TD>
                            <TD textAlign='left'><TextWithTooltip title={email}>{email}</TextWithTooltip></TD>
                            <TD><input type='checkbox' id={`${id}`} checked={assign_to_roi} onChange={onChangeInput} /></TD>
                          </TR>
                        ))
                      }
                    </tbody>
                  </Table>
                </TableWrapper>
              </>
        }
      </TableContainer>
      {(!loading && emails && emails.length > 0) &&
        <CenterAlignedContainer>
          <LeftButton design='secondary' onClick={onCancel}>{t('Common:cancel')}</LeftButton>
          <Button onClick={onSave}>{t('Common:save')}</Button>
        </CenterAlignedContainer>}
      {
        (alert.message !== '' &&
          <AlertContainer>
            <AlertBar type={alert.type} message={alert.message} />
          </AlertContainer>
        )
      }
    </PopupContainer>

  );
};
export default EmailTargets;
